/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import {
    Button,
    Checkbox,
    Input,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import { IClaim } from "../../../claims/types/claims.types";
import { useFetchClaims } from "../../../claims/hooks/claims.queries";
import { useDebounce } from "../../../../hooks/helpers";
import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import ListState from "../../../../components/ListState/ListState";
import Loader from "../../../../shared-ui/Loader/Loader";
import useToast from "../../../../hooks/useToast";
import { useReassignRemitClaimLinks } from "../../hooks/remits.mutations";
import { IRemit } from "../../types/remits.types";

interface MoveRemitFormProps {
    onFormSubmit(): void;
    remits: IRemit[];
}

export default function MoveRemitForm({
    onFormSubmit,
    remits,
}: MoveRemitFormProps) {
    const primaryRemit = remits[0];
    const sourceClaimId = primaryRemit.claim_id;
    const sourceClientId = primaryRemit.client.client_id;
    const sourceDosIso = primaryRemit.date_of_service;
    const sourceDosKey = sourceDosIso.slice(0, 10);
    const sourceClientName = displayNameInRightFormat({
        firstName: primaryRemit.client.first_name,
        lastName: primaryRemit.client.last_name,
    });
    const sourceCpts = remits
        .map((r) => r.remittance_cpt_code || r.cpt_code)
        .filter(Boolean);

    const [searchString, setSearchString] = React.useState(sourceClientName);
    const [selectedTargetClaimId, setSelectedTargetClaimId] = React.useState<
        string | null
    >(null);
    const [reason, setReason] = React.useState("");
    const [reasonError, setReasonError] = React.useState<string | null>(null);

    const debouncedSearchString = useDebounce(searchString, 500);

    const reassignMutation = useReassignRemitClaimLinks();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { data, isFetching, isSuccess, error } = useFetchClaims(
        {
            search_string: debouncedSearchString,
            note_dos_from: sourceDosKey,
            note_dos_to: sourceDosKey,
            show_voided: "false",
        },
        Boolean(debouncedSearchString)
    );

    const eligibleClaims = React.useMemo(() => {
        return (data?.data || []).filter(
            (claim) =>
                claim.claim_id !== sourceClaimId &&
                claim.client_id === sourceClientId &&
                !claim.is_voided
        );
    }, [data?.data, sourceClaimId, sourceClientId]);

    const handleSubmit = () => {
        if (!selectedTargetClaimId) {
            toast({
                mode: "error",
                message: "Select a claim to move this remit to",
            });
            return;
        }
        const trimmedReason = reason.trim();
        if (!trimmedReason) {
            setReasonError("Reason is required");
            return;
        }

        reassignMutation.mutate(
            {
                source_claim_id: sourceClaimId,
                target_claim_id: selectedTargetClaimId,
                remittance_ids: remits.map((r) => r.remittance_id),
                reason: trimmedReason,
            },
            {
                onSuccess: async (res) => {
                    await queryClient.invalidateQueries({
                        queryKey: ["remit-claims"],
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ["get-claim"],
                    });

                    toast({
                        mode: "success",
                        message:
                            res.message ||
                            "Remit claim links reassigned successfully",
                    });

                    onFormSubmit();
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not move remit at this time",
                    });
                },
            }
        );
    };

    const columns: ColumnDef<IClaim>[] = [
        {
            id: "select",
            header: "",
            cell: ({ row }) => {
                const isSelected =
                    selectedTargetClaimId === row.original.claim_id;
                return (
                    <Checkbox
                        label=""
                        name="target_claim_id"
                        value={row.original.claim_id}
                        isChecked={isSelected}
                        handleChange={() =>
                            setSelectedTargetClaimId(
                                isSelected ? null : row.original.claim_id
                            )
                        }
                    />
                );
            },
        },
        {
            accessorKey: "external_reference_id",
            header: "Claim ID",
            cell: ({ row }) => row.original.external_reference_id ?? "--",
        },
        {
            accessorKey: "date_of_service",
            header: "Date of Service",
            cell: ({ row }) => formatDate(row.original.date_of_service),
        },
        {
            accessorKey: "cpt_code",
            header: "CPT code",
        },
        {
            accessorKey: "charge_amount",
            header: "Amount",
            cell: ({ row }) => showMoneyInAppFormat(row.original.charge_amount),
        },
        {
            accessorKey: "claim_status",
            header: "Claim Status",
            cell: ({ row }) =>
                makeStringFirstLetterCapital(
                    removeEnumUnderscore(row.original.claim_status)
                ),
        },
        {
            accessorKey: "insurance_name",
            header: "Insurance",
            cell: ({ row }) => (
                <span className="uppercase">
                    {makeStringFirstLetterCapital(row.original.insurance_name)}
                </span>
            ),
        },
    ];

    return (
        <div>
            <div className="mb-[--spacing-items] grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-y-3 px-16 py-12 rounded-md bg-[--card-bg-gray]">
                <div className="flex flex-col gap-y-1">
                    <span className="text-textSec text-xs uppercase font-medium">
                        Client
                    </span>
                    <span className="text-sm capitalize">
                        {sourceClientName}
                    </span>
                </div>
                <div className="flex flex-col gap-y-1">
                    <span className="text-textSec text-xs uppercase font-medium">
                        Date of service
                    </span>
                    <span className="text-sm">{formatDate(sourceDosIso)}</span>
                </div>
                <div className="flex flex-col gap-y-1">
                    <span className="text-textSec text-xs uppercase font-medium">
                        Source claim
                    </span>
                    <span className="text-sm">
                        {primaryRemit.claim_number || "--"}
                    </span>
                </div>
                <div className="flex flex-col gap-y-1">
                    <span className="text-textSec text-xs uppercase font-medium">
                        {sourceCpts.length === 1 ? "CPT" : "CPTs"}
                    </span>
                    <span className="text-sm">
                        {sourceCpts.length > 0 ? sourceCpts.join(", ") : "--"}
                    </span>
                </div>
            </div>

            <div className="px-16">
                <SearchInput
                    placeholder="Search by client name or claim ID"
                    containerClass="mb-[--spacing-items]"
                    defaultValue={sourceClientName}
                    onChange={(e) => {
                        setSearchString(e.target.value);
                        setSelectedTargetClaimId(null);
                    }}
                />

                {error && error?.response?.status !== 404 && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg="Cannot display claims at this time please try again later"
                        context="general"
                    />
                )}

                {isFetching && <Loader />}

                {!isFetching &&
                    isSuccess &&
                    debouncedSearchString &&
                    eligibleClaims.length === 0 && (
                        <ListState
                            stateHelperText="Try a different search — claims must be for the same client and date of service"
                            isError={false}
                            emptyMessage="No eligible target claims found"
                            context="general"
                        />
                    )}

                {!isFetching && eligibleClaims.length > 0 && (
                    <Table columns={columns} data={eligibleClaims} />
                )}

                <div className="mt-[--spacing-items]">
                    <Input
                        name="reason"
                        label="Reason"
                        value={reason}
                        onChange={(e) => {
                            setReason(e.target.value);
                            if (reasonError) setReasonError(null);
                        }}
                        hasError={!!reasonError}
                        errorText={reasonError ?? undefined}
                    />
                </div>

                <div className="mt-[--spacing-items] flex items-center justify-end gap-[--spacing-items]">
                    <Button
                        variant="secondary"
                        type="button"
                        onClick={onFormSubmit}
                        disabled={reassignMutation.isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={
                            !selectedTargetClaimId || reassignMutation.isLoading
                        }
                    >
                        Move remit
                    </Button>
                </div>
            </div>
        </div>
    );
}
